import './ActionSheet.sass';

import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import { ActionSheetProps, ActionSheetPropsOption } from './ActionSheet.interface';
import { classNames } from '$utils';
import { IcBack28, IcClose28 } from '$assets';

const getCurrentOptions = (options: ActionSheetPropsOption[], path: any[]) => {
  const currentPath = [...path];
  const value = currentPath.shift();

  if (path.length) {
    const currentOptions = options.find((option) => option.value == value)?.options;
    if (currentPath.length && currentOptions) {
      return getCurrentOptions(currentOptions, currentPath);
    }
    return currentOptions;
  }
  return options;
};

export const ActionSheetContext = React.createContext({
  active: false,
  setActive: (value: boolean) => {},
});

export const ActionSheetProvider: FC = ({ children }) => {
  const [active, setActive] = useState(false);
  return (
    <ActionSheetContext.Provider value={{ active, setActive }}>
      {children}
    </ActionSheetContext.Provider>
  );
};

export const ActionSheet: FC<ActionSheetProps> = ({
  isOpened,
  options,
  onChange,
  selected,
  className,
}) => {
  const node = useRef(document.getElementById('action_sheet'));
  const [path, setPath] = useState<any[]>([]);
  const { setActive } = useContext(ActionSheetContext);

  useEffect(() => {
    setActive(isOpened);
    return () => setActive(false);
  }, [isOpened]);

  const handleOption = useCallback(
    (option: ActionSheetPropsOption) => (e) => {
      e.stopPropagation();
      if (option.disabled) {
        return;
      }
      if (option.options) {
        setPath([...path, option.value]);
        return false;
      }
      onChange(option.value);
      setPath([]);
    },
    [onChange, path, setPath],
  );

  const handleClose = useCallback(
    (e?: React.MouseEvent<HTMLElement>) => {
      e?.stopPropagation();
      path.length ? setPath(path.slice(0, -1)) : onChange(selected);
    },
    [onChange, selected, path, setPath],
  );

  useEffect(() => {
    const escape = (e: KeyboardEvent) => {
      e.key === 'Escape' && handleClose();
    };

    if (isOpened) {
      window.addEventListener('keydown', escape, false);
    }
    return () => {
      window.removeEventListener('keydown', escape, false);
    };
  }, [isOpened, handleClose]);

  const currentOptions = useMemo(() => getCurrentOptions(options, path), [options, path]);

  if (!isOpened || !node.current) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={classNames('ActionSheet', {
        [className || '']: className,
      })}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="ActionSheet__options">
        {currentOptions.map((option) => (
          <div
            className={classNames({
              ActionSheet__option: true,
              selected: option.value === selected,
            })}
            key={option.value}
            onClick={handleOption(option)}
          >
            {option.label}
            {option.children || null}
          </div>
        ))}
      </div>
      <div className="ActionSheet__close" onClick={handleClose}>
        {path.length ? (
          <IcBack28 className="ActionSheet__close__iconBack" />
        ) : (
          <IcClose28 className="ActionSheet__close__iconClose" />
        )}
      </div>
    </div>,
    node.current,
  );
};
